import { DateStringValue } from '@unistocks/components/calendar/types';

/**
 * Wrap a per-date prop getter so each distinct date is resolved at most once.
 *
 * Returns `undefined` when `getter` is `undefined`, preserving the optionality
 * of the original prop so call sites keep their `getter?.(date)` shape and their
 * result stays `T | undefined`. Call this ONCE PER RENDER and share the result
 * with every consumer in that render — see the note above on why the cache must
 * not be memoized across renders.
 */
export function memoizeByDate<T>(
  getter: ((date: DateStringValue) => T) | undefined
): ((date: DateStringValue) => T) | undefined {
  if (!getter) {
    return undefined;
  }

  const cache = new Map<DateStringValue, T>();

  return (date: DateStringValue): T => {
    const cached = cache.get(date);
    // `has` (not a truthiness test) so a getter legitimately returning
    // `undefined`/`false` is still cached rather than recomputed every hit.
    if (cached !== undefined || cache.has(date)) {
      return cached as T;
    }

    const value = getter(date);
    cache.set(date, value);
    return value;
  };
}
